import {useLazyGetLatestOffersQuery} from '../services/ibeApi'
import {useAppDispatch, useAppSelector} from '../store/hooks'
import {selectPropertyId, updateItem} from '../store/slices/shoppingCartSlice'
import {getLogger} from '../services/logging'
import {selectPropertyById} from '../store/slices/configSlice'
import {ShoppingCartItem} from '../schema'
import {formatISODate, toPropertyDateTime} from '../services/dateUtils'
import {useCallback} from 'react'

const logger = getLogger('useCartItemRefresh')

export const useCartItemRefresh = () => {
  const dispatch = useAppDispatch()
  const [triggerFetch] = useLazyGetLatestOffersQuery()
  const propertyId = useAppSelector(selectPropertyId)
  const propertyConfig = useAppSelector(selectPropertyById(propertyId))
  const timeZone = propertyConfig?.timeZone

  const refreshCartItem = useCallback(
    async (cartItem: ShoppingCartItem): Promise<void> => {
      logger.info({
        message: 'Refresh the latest pricing for shopping cart item...',
        data: {
          cartItem
        }
      })

      const ratePlanId = cartItem.offer?.ratePlan.id
      const arrival = formatISODate(
        toPropertyDateTime(cartItem.offer?.arrival, timeZone)
      )
      const departure = formatISODate(
        toPropertyDateTime(cartItem.offer?.departure, timeZone)
      )
      const guests = cartItem.guests

      const latestOfferRequest = {
        arrival,
        departure,
        ratePlanId,
        guests
      }

      logger.info({
        message: 'Getting the latest offer for...',
        data: {
          latestOfferRequest
        }
      })

      const response = await triggerFetch({
        requests: [latestOfferRequest],
        lastUpdated: new Date().toISOString()
      })

      const latestOffers = response.data || []

      if (latestOffers.length === 1) {
        const latestOffer = latestOffers[0]
        const updatedCartItem = {
          ...cartItem,
          offer: latestOffer
        }
        logger.info({
          message:
            'Successfully retrieved latest offer, updating shopping cart item with latest prices...',
          data: {
            updatedCartItem
          }
        })

        dispatch(updateItem(updatedCartItem))
      } else {
        const errorMsg = `Failed to retrieve latest offer for cart item ${cartItem.id}`
        logger.error({
          message: errorMsg,
          data: {
            cartItem
          }
        })
        throw new Error(errorMsg)
      }
    },
    [triggerFetch, propertyConfig]
  )

  return {refreshCartItem}
}

export default useCartItemRefresh
